/**
 * Single source of truth for the `property_tasks` collection so the two
 * writers (the Property Tasks page and the property detail page) can't drift
 * on doc shape or the `createdBy == caller uid` security rule.
 */
import { useEffect, useState } from "react";
import { addDoc, collection, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getUserLabel } from "./userDisplay";

const COLLECTION = "property_tasks";

// Map a list of assignee uids -> their display labels from a user list.
export const taskAssigneeNames = (ids, users) =>
  (ids || []).map((uid) => {
    const u = (users || []).find((x) => x._id === uid);
    return u ? getUserLabel(u) : uid;
  });

// Create a property_task. `deadline` is a dayjs (or null); `assignedTo` is a
// uid[]; `users` resolves those uids to names; `createdByName` is the caller's
// display name (falls back to the Firebase auth identity).
export const createPropertyTask = ({
  title,
  description,
  propertyId,
  propertyName,
  deadline,
  assignedTo,
  users,
  createdByName,
  vendorId,
  vendorName,
}) => {
  const ids = assignedTo || [];
  return addDoc(collection(db, COLLECTION), {
    title: (title || "").trim(),
    description: (description || "").trim(),
    propertyId: propertyId || null,
    propertyName: propertyName || null,
    deadline: deadline ? deadline.toISOString() : null,
    status: "open",
    // Empty = unassigned; the notify function then alerts the admins to assign it.
    assignedTo: ids,
    assignedToNames: taskAssigneeNames(ids, users),
    // Optional linked vendor (denormalized name survives a vendor rename/delete).
    vendorId: vendorId || null,
    vendorName: vendorName || null,
    // Rules require createdBy == the caller's uid.
    createdBy: auth.currentUser?.uid || null,
    createdByName:
      createdByName ||
      auth.currentUser?.displayName ||
      auth.currentUser?.email ||
      "Admin",
    createdAt: new Date().toISOString(),
    completedAt: null,
    completedBy: null,
  });
};

// Shared task-state predicates — single source of truth so the Property Tasks
// page filters, the dashboard count, and the sidebar badge can't drift.
export const isTaskOpen = (t) => t?.status !== "done";
export const isTaskUnassigned = (t) =>
  !(Array.isArray(t?.assignedTo) && t.assignedTo.length) && !t?.assignTo;
// Open task with nobody assigned.
export const needsAssignment = (t) => isTaskOpen(t) && isTaskUnassigned(t);
// Open task awaiting a reply (hasOpenQuestion maintained by notifyOnTaskNote).
export const hasOpenQuestion = (t) => isTaskOpen(t) && !!t?.hasOpenQuestion;
// Needs admin attention = an open question OR unassigned (counted once).
export const needsAttention = (t) => hasOpenQuestion(t) || needsAssignment(t);

// Live count of tasks needing admin attention — drives the sidebar Tasks badge
// so admins see waiting work from any screen. A task counts (once) if it's open
// AND either:
//   - has an unanswered question (`hasOpenQuestion`, maintained server-side by
//     notifyOnTaskNote — set when an inspector asks, cleared on a non-asker
//     reply), or
//   - is unassigned (nobody in `assignedTo[]` and no legacy `assignTo`).
// Mirrors the `hasOpenQuestion(t)` / `needsAssignment(t)` predicates on the
// Property Tasks page. A task that is both unassigned and has a question still
// counts once — the badge is "tasks needing attention", not a sum of issues.
//
// `adminUid` gates the subscription the same way useNotificationListener does:
// redux userData rehydrates from localStorage before Firebase auth finishes
// restoring, so subscribing on bare mount can fire a Firestore read with no
// token → permission-denied, and a denied onSnapshot is terminated for good
// (no auto-retry). Waiting for adminUid means the listener (re)subscribes once
// auth is actually present, and tearing down on a uid change recovers cleanly.
export const useTaskAttentionCount = (adminUid) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!adminUid) {
      setCount(0);
      return undefined;
    }
    // Only stream NON-DONE tasks: done tasks accumulate forever and never count
    // toward attention, so there's no reason to sync the whole collection to
    // every admin on every screen. Single-field inequality — no composite index.
    const unsubscribe = onSnapshot(
      query(collection(db, COLLECTION), where("status", "!=", "done")),
      (snapshot) => {
        let open = 0;
        snapshot.forEach((d) => {
          if (needsAttention(d.data())) open += 1;
        });
        setCount(open);
      },
      (error) => {
        // e.g. transient permission-denied while auth is restoring or after
        // sign-out; log instead of throwing an unhandled listener error.
        console.warn("task-attention listener stopped:", error?.message);
      },
    );
    return unsubscribe;
  }, [adminUid]);
  return count;
};
